import React, { ReactElement, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import dayjs from 'dayjs'

import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
} from '@mui/material'

import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  selectRecertificationCycles,
  selectRecertificationCategories,
  selectRecertificationCategoriesStatus,
  fetchRecertificationCycleCategories,
  selectCourses,
  selectCoursesStatus,
  fetchCourses,
  fetchLicenseRecertificationCycles,
  fetchNREMTRecertificationCycles,
  fetchCredentialRecertificationCycles,
  selectLicenseRecertificationCyclesStatus,
  selectCredentialRecertificationCyclesStatus,
  selectNREMTRecertificationCyclesStatus,
  selectCompletedEvents,
  selectCompletedEventsStatus,
  fetchCompletedEvents,
  selectExternalCertificates,
  selectExternalCertificatesStatus,
  fetchExternalCertificates,
  selectEligibleEventsStatus,
  selectEligibleEventsByRecertCycleID,
  fetchEligibleEvents,
} from 'src/v3/profile/profile.slice'
import { selectUseRecertificationCycle } from 'src/v3/settings/settings.slice'

import {
  CalendarIcon,
  CertificateIcon,
  CourseIcon,
  DeleteIcon,
  DragAndDropIcon,
  EditIcon,
  EventIcon,
  ExpandIcon,
  LaunchIcon,
  LeftArrowIcon,
  ManagerIcon,
  VerifiedIcon,
} from 'src/v3/core/icons'
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd'
import type { DropResult } from '@hello-pangea/dnd'
import axios, { pluckError } from 'src/utilities/http-client'
import { useNotifications } from 'src/v3/hooks/use-notifications'
import {
  groupsInOrder,
  itemsInGroupOrder,
  moveItem,
} from 'src/v3/utilities/category-order'
import AddIcon from '@mui/icons-material/Add'
import InfoIcon from '@mui/icons-material/Info'
import CategoryIcon from '@mui/icons-material/Category'
import CheckedIcon from '@mui/icons-material/CheckCircle'
import TemplateIcon from '@mui/icons-material/Ballot'
import UncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'

import {
  RecertificationCycleCategory,
  RecertificationCycleCourse,
  RecertificationCycleEvent,
  RecertificationCycleExternalCertificate,
} from 'src/v3/core/types'
import { useAutoFetch } from 'src/v3/hooks/use-auto-fetch'
import BasePage from '../layout'
import { CategoryForm } from './components/category-form'
import { CourseForm } from './components/course-form'
import { MultiCircularProgress } from './components/progress'
import { ConfirmDeletion } from './components/confirm-deletion'
import { EventForm } from './components/event-form'
import { ExternalCertificateForm } from './components/external-certificate-form'
import { TemplateForm } from './components/template-form'
import { getCycleStatus } from '.'

const UNGROUPED = 'Other'
const GROUP_DRAG_TYPE = 'group'
const CATEGORY_DROPPABLE_PREFIX = 'category:'

export let CycleDetail = (): ReactElement => {
  let dispatch = useAppDispatch()
  let navigate = useNavigate()
  let { cycleID } = useParams()
  let useRecertificationCycle = useAppSelector(selectUseRecertificationCycle)

  let allCycles = useAppSelector(selectRecertificationCycles)
  let licensesStatus = useAppSelector(selectLicenseRecertificationCyclesStatus)
  let credentialsStatus = useAppSelector(
    selectCredentialRecertificationCyclesStatus,
  )
  let nremtStatus = useAppSelector(selectNREMTRecertificationCyclesStatus)
  let allCategories = useAppSelector(selectRecertificationCategories)
  let categoriesStatus = useAppSelector(selectRecertificationCategoriesStatus)
  let allCompletedEvents = useAppSelector(selectCompletedEvents)
  let completedEventsStatus = useAppSelector(selectCompletedEventsStatus)
  let eligibleEvents = useAppSelector(
    selectEligibleEventsByRecertCycleID(cycleID || ''),
  )
  let eligibleEventsStatus = useAppSelector(selectEligibleEventsStatus)
  let courses = useAppSelector(selectCourses)
  let coursesStatus = useAppSelector(selectCoursesStatus)
  let externalCertificates = useAppSelector(selectExternalCertificates)
  let externalCertificatesStatus = useAppSelector(
    selectExternalCertificatesStatus,
  )

  let [templateFormOpen, setTemplateFormOpen] = useState<boolean>(false)
  let [categoryFormOpen, setCategoryFormOpen] = useState<boolean>(false)
  let [categoryFormInfo, setCategoryFormInfo] =
    useState<Partial<RecertificationCycleCategory>>()
  let [courseFormOpen, setCourseFormOpen] = useState<boolean>(false)
  let [courseFormInfo, setCourseFormInfo] =
    useState<Partial<RecertificationCycleCourse>>()
  let [eventFormOpen, setEventFormOpen] = useState<boolean>(false)
  let [eventFormInfo, setEventFormInfo] =
    useState<Partial<RecertificationCycleEvent>>()
  let [externalCertFormOpen, setExternalCertFormOpen] = useState<boolean>(false)
  let [externalCertFormInfo, setExternalCertFormInfo] =
    useState<Partial<RecertificationCycleExternalCertificate>>()
  let [deleteCategoryID, setDeleteCategoryID] = useState<string>()
  let [deleteCourseID, setDeleteCourseID] = useState<string>()
  let [deleteEventID, setDeleteEventID] = useState<string>()
  let [deleteExternalCertID, setDeleteExternalCertID] = useState<string>()
  let [deleting, setDeleting] = useState<boolean>(false)
  let [isSavingOrder, setIsSavingOrder] = useState(false)
  let { showError, NotificationComponent } = useNotifications()

  useAutoFetch(coursesStatus, fetchCourses)
  useAutoFetch(externalCertificatesStatus, fetchExternalCertificates)
  useAutoFetch(licensesStatus, fetchLicenseRecertificationCycles)
  useAutoFetch(credentialsStatus, fetchCredentialRecertificationCycles)
  useAutoFetch(nremtStatus, fetchNREMTRecertificationCycles)

  useEffect(() => {
    if (
      cycleID &&
      (!categoriesStatus[cycleID] ||
        categoriesStatus[cycleID] === 'uninitialized')
    ) {
      dispatch(fetchRecertificationCycleCategories(cycleID))
    }
  }, [cycleID, categoriesStatus])

  useEffect(() => {
    if (
      cycleID &&
      (!completedEventsStatus[cycleID] ||
        completedEventsStatus[cycleID] === 'uninitialized')
    ) {
      dispatch(fetchCompletedEvents(cycleID))
    }
  }, [cycleID, completedEventsStatus])

  useEffect(() => {
    if (
      cycleID &&
      (!eligibleEventsStatus[cycleID] ||
        eligibleEventsStatus[cycleID] === 'uninitialized')
    ) {
      dispatch(fetchEligibleEvents(cycleID))
    }
  }, [cycleID, eligibleEventsStatus])

  let cycle = allCycles.find((c) => c.id === cycleID)
  let completedEvents = allCompletedEvents[cycleID || ''] || []
  let categories = (allCategories[cycleID || ''] || [])
    .filter((c) => c.categoryType === 'standard')
    .toSorted((a, b) => a.sequence - b.sequence)

  // Shown in local order so a drag lands at once; the server's order takes over once saved.
  let [localCategories, setLocalCategories] = useState(categories)

  useEffect(() => {
    if (!isSavingOrder) setLocalCategories(categories)
  }, [allCategories, cycleID, isSavingOrder])
  let categoriesCompleted = categories.filter(
    (cat) =>
      cat.courses.reduce((sum, c) => sum + (c.complete ? c.hours : 0), 0) +
        cat.events.reduce((sum, e) => sum + e.hours, 0) +
        cat.externalCertificates.reduce((sum, ec) => sum + ec.hours, 0) >=
      cat.hours,
  ).length
  let groupOf = (c: RecertificationCycleCategory) => c.subcategory || UNGROUPED
  let groups = groupsInOrder(localCategories, groupOf, (c) => c.sequence)
  let hasGroups = groups.some((g) => g !== UNGROUPED)

  // Renumbers the whole cycle in the given order: groups and their categories move together.
  let saveOrder = (reordered: RecertificationCycleCategory[]) => {
    if (!cycleID) return
    setLocalCategories(reordered.map((c, i) => ({ ...c, sequence: i })))
    setIsSavingOrder(true)
    axios
      .put(`/api/profile/recertification-cycle/${cycleID}/category/sequence`, {
        categories: reordered.map((c, i) => ({
          categoryID: c.id,
          sequence: i,
        })),
      })
      .catch((err) =>
        showError(pluckError(err, 'Failed to update category order')),
      )
      // Saving lasts until the refetch lands (as in Create v5), so the old order never flashes back.
      .then(() => dispatch(fetchRecertificationCycleCategories(cycleID)))
      .finally(() => setIsSavingOrder(false))
  }

  // A group drags as a block; a category moves within its group.
  let handleCategoryDragEnd = (result: DropResult) => {
    let { source, destination } = result
    if (
      !destination ||
      (destination.droppableId === source.droppableId &&
        destination.index === source.index)
    )
      return
    if (result.type === GROUP_DRAG_TYPE) {
      saveOrder(
        itemsInGroupOrder(
          localCategories,
          moveItem(groups, source.index, destination.index),
          groupOf,
        ),
      )
      return
    }
    if (destination.droppableId !== source.droppableId) return
    let group = source.droppableId.slice(CATEGORY_DROPPABLE_PREFIX.length)
    let reorderedGroup = moveItem(
      localCategories.filter((c) => groupOf(c) === group),
      source.index,
      destination.index,
    )
    saveOrder(
      groups.flatMap((g) =>
        g === group
          ? reorderedGroup
          : localCategories.filter((c) => groupOf(c) === g),
      ),
    )
  }
  let allCourses = categories.flatMap((cat) => cat.courses)
  let allEvents = categories.flatMap((cat) => cat.events)
  let allExternalCerts = categories.flatMap((cat) => cat.externalCertificates)
  let completedHours =
    allCourses.reduce((sum, c) => sum + (c.complete ? c.hours : 0), 0) +
    allEvents.reduce((sum, e) => sum + e.hours, 0) +
    allExternalCerts.reduce((sum, ec) => sum + ec.hours, 0)
  let totalHours = categories.reduce((sum, cat) => sum + cat.hours, 0)
  let liveCategory = (allCategories[cycleID || ''] || []).find(
    (c) => c.categoryType === 'live',
  )
  let completedEventHours =
    completedEvents.reduce((sum, e) => sum + e.hours, 0) +
    (liveCategory?.externalCertificates.reduce(
      (sum, ec) => sum + ec.hours,
      0,
    ) || 0)
  let liveCompleted = !liveCategory
    ? false
    : completedEventHours >= liveCategory.hours

  let baseRefetch =
    cycle?.entity.kind === 'License'
      ? fetchLicenseRecertificationCycles()
      : cycle?.entity.kind === 'Credential'
        ? fetchCredentialRecertificationCycles()
        : fetchNREMTRecertificationCycles()
  let refetches = [
    fetchRecertificationCycleCategories(cycleID || ''),
    baseRefetch,
  ]

  let LiveClassList = ({
    category,
  }: {
    category: RecertificationCycleCategory
  }) => {
    let [open, setOpen] = useState<boolean>(category.events.length > 0)

    return (
      <Box
        sx={{
          mt: 1,
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            pb: 0.25,
          }}
        >
          <Typography
            color="text.secondary"
            sx={{
              fontWeight: 'bold',
              flexGrow: 0,
              ml: 1,
            }}
          >
            Live Classes
          </Typography>
          <Box sx={{ flexGrow: 1, px: 0.5 }}>
            <IconButton
              size="small"
              aria-label={
                open ? 'Collapse live classes' : 'Expand live classes'
              }
              aria-expanded={open}
              onClick={() => setOpen(!open)}
            >
              <ExpandIcon
                sx={{ transform: !open ? 'rotate(180deg)' : 'none' }}
              />
            </IconButton>
          </Box>
          <Button
            color="primary"
            size="small"
            onClick={() => {
              setEventFormInfo({
                categoryID: category.id,
              })
              setEventFormOpen(true)
            }}
          >
            Add Live Class
          </Button>
        </Box>
        {open && (
          <Box
            sx={{
              flexGrow: 1,
              border: '1px solid',
              borderColor: 'divider',
              minHeight: !category.events.length ? 192 : 96,
              maxHeight: 288,
              overflow: 'scroll',
            }}
          >
            {!category.events.length && (
              <Box
                sx={{
                  p: 1,
                  height: 192,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignContent: 'center',
                }}
              >
                <Avatar
                  sx={{
                    mb: 0.5,
                    height: 48,
                    width: 48,
                    bgcolor: 'background.paper',
                    alignSelf: 'center',
                  }}
                >
                  <EventIcon fontSize="medium" color="primary" />
                </Avatar>
                <Typography align="center">
                  No Live Classes Added Yet
                </Typography>
                <Button
                  sx={{ alignSelf: 'center', mt: 1 }}
                  variant="contained"
                  startIcon={<AddIcon />}
                  disabled={!categories.length}
                  onClick={() => {
                    setEventFormInfo({
                      categoryID: category.id,
                    })
                    setEventFormOpen(true)
                  }}
                >
                  Add Live Class
                </Button>
              </Box>
            )}
            {category.events.map((certData, i) => {
              let { id, eventID, hours } = certData
              let event = eligibleEvents.find((ec) => ec.id === eventID)

              return (
                <Box
                  key={certData.eventID}
                  sx={{
                    p: 1.5,
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 0.5,
                    bgcolor: i % 2 ? 'divider' : undefined,
                  }}
                >
                  <CheckedIcon color="success" aria-hidden="true" />
                  <span className="sr-only">Completed</span>
                  <Typography sx={{ flexGrow: 1 }}>{event?.name}</Typography>
                  <Typography sx={{ flexShrink: 0 }}>
                    {hours} Hour{hours === 1 ? '' : 's'}
                  </Typography>
                  <Box
                    sx={{
                      my: -1,
                      ml: 1.5,
                      mr: -0.5,
                      flexShrink: 0,
                    }}
                  >
                    <IconButton
                      size="small"
                      aria-label={`Edit live class${event?.name ? `: ${event.name}` : ''}`}
                      onClick={() => {
                        setEventFormInfo({
                          ...certData,
                          categoryID: category.id,
                        })
                        setEventFormOpen(true)
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label={`Delete live class${event?.name ? `: ${event.name}` : ''}`}
                      onClick={() => setDeleteEventID(id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              )
            })}
            {category.events.length === 1 && <Divider />}
          </Box>
        )}
      </Box>
    )
  }

  let ExternalCertificateList = ({
    category,
  }: {
    category: RecertificationCycleCategory
  }) => {
    let [open, setOpen] = useState<boolean>(
      category.externalCertificates.length > 0,
    )

    return (
      <Box
        sx={{
          mt: 1,
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            pb: 0.25,
          }}
        >
          <Typography
            color="text.secondary"
            sx={{
              fontWeight: 'bold',
              flexGrow: 0,
              ml: 1,
            }}
          >
            {category.categoryType === 'live'
              ? 'Live External'
              : 'External Certificates'}
          </Typography>
          <Box sx={{ flexGrow: 1, px: 0.5 }}>
            <IconButton
              size="small"
              aria-label={
                open
                  ? 'Collapse external certificates'
                  : 'Expand external certificates'
              }
              aria-expanded={open}
              onClick={() => setOpen(!open)}
            >
              <ExpandIcon
                sx={{ transform: !open ? 'rotate(180deg)' : 'none' }}
              />
            </IconButton>
          </Box>
          <Button
            color="secondary"
            size="small"
            onClick={() => {
              setExternalCertFormInfo({
                categoryID: category.id,
              })
              setExternalCertFormOpen(true)
            }}
          >
            Add External Certificate
          </Button>
        </Box>
        {open && (
          <Box
            sx={{
              flexGrow: 1,
              border: '1px solid',
              borderColor: 'divider',
              minHeight: !category.externalCertificates.length ? 192 : 96,
              maxHeight: 288,
              overflow: 'scroll',
            }}
          >
            {!category.externalCertificates.length && (
              <Box
                sx={{
                  p: 1,
                  height: 192,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignContent: 'center',
                }}
              >
                <Avatar
                  sx={{
                    mb: 0.5,
                    height: 48,
                    width: 48,
                    bgcolor: 'background.paper',
                    alignSelf: 'center',
                  }}
                >
                  <CertificateIcon fontSize="medium" color="primary" />
                </Avatar>
                <Typography align="center">
                  No External Certificates Added Yet
                </Typography>
                <Button
                  sx={{ alignSelf: 'center', mt: 1 }}
                  variant="contained"
                  startIcon={<AddIcon />}
                  disabled={!categories.length}
                  onClick={() => {
                    setExternalCertFormInfo({
                      categoryID: category.id,
                    })
                    setExternalCertFormOpen(true)
                  }}
                >
                  Add External Certificate
                </Button>
              </Box>
            )}
            {category.externalCertificates.map((certData, i) => {
              let { id, externalCertificateID, hours } = certData
              let externalCert = externalCertificates.find(
                (ec) => ec.id === externalCertificateID,
              )

              return (
                <Box
                  key={certData.externalCertificateID}
                  sx={{
                    p: 1.5,
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 0.5,
                    bgcolor: i % 2 ? 'divider' : undefined,
                  }}
                >
                  <CheckedIcon color="success" aria-hidden="true" />
                  <span className="sr-only">Completed</span>
                  <Typography sx={{ flexGrow: 1 }}>
                    {externalCert?.name}
                  </Typography>
                  <Typography sx={{ flexShrink: 0 }}>
                    {hours} Hour{hours === 1 ? '' : 's'}
                  </Typography>
                  <Box sx={{ my: -1, ml: 1.5, mr: -0.5, flexShrink: 0 }}>
                    <IconButton
                      size="small"
                      aria-label={`Edit external certificate${externalCert?.name ? `: ${externalCert.name}` : ''}`}
                      onClick={() => {
                        setExternalCertFormInfo({
                          ...certData,
                          categoryID: category.id,
                        })
                        setExternalCertFormOpen(true)
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      aria-label={`Delete external certificate${externalCert?.name ? `: ${externalCert.name}` : ''}`}
                      onClick={() => setDeleteExternalCertID(id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              )
            })}
            {category.externalCertificates.length === 1 && <Divider />}
          </Box>
        )}
      </Box>
    )
  }

  return (
    <BasePage>
      {cycle && useRecertificationCycle && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {cycle.complete && (
              <VerifiedIcon fontSize="large" color="success" sx={{ mr: -1 }} />
            )}
            <Typography variant="h4" component="h2">
              {cycle.name}
            </Typography>
            <Chip
              label={getCycleStatus(cycle)}
              color={cycle.complete ? 'success' : 'primary'}
            />
            <Box>
              <IconButton
                color="error"
                aria-label="Delete recertification cycle"
                sx={{
                  border: '1px solid',
                  borderColor: 'error',
                }}
                size="small"
                onClick={() => setDeleting(true)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
              <ConfirmDeletion
                name="recertification cycle"
                extraText="All progress made against this cycle will be lost."
                route={`/api/profile/recertification-cycle/cycle/${cycleID}`}
                fetchEntity={[baseRefetch]}
                navigateURL="/profile/recertification"
                open={deleting}
                setOpen={() => setDeleting(false)}
              />
            </Box>
            <Box sx={{ flexGrow: 1 }}></Box>
            <Button
              color="inherit"
              startIcon={<LeftArrowIcon />}
              onClick={() => navigate('/profile/recertification')}
            >
              Back to All Cycles
            </Button>
          </Box>
          <Typography
            color="text.secondary"
            variant="subtitle2"
            sx={{ mt: 0.5, ml: 0 }}
          >
            <CalendarIcon sx={{ mb: 0.5, mr: 0.5, height: 18, width: 18 }} />
            {dayjs(cycle.issueDate).format('MMMM D, YYYY')} -{' '}
            {dayjs(cycle.expirationDate).format('MMMM D, YYYY')}
          </Typography>
          <Typography
            color="text.secondary"
            variant="subtitle1"
            sx={{ mt: 0.5 }}
          >
            Build out your recertification cycle, including categories with
            their hour requirements. Specific courses can be assigned to their
            categories at your leisure.
          </Typography>
          {cycle.description && (
            <Typography
              color="textSecondary"
              variant="body2"
              sx={{ mt: 1, whiteSpace: 'pre-line' }}
            >
              {cycle.description}
            </Typography>
          )}
          <Box
            sx={{
              my: 2,
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 2,
            }}
          >
            <Card
              variant="outlined"
              sx={{
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Typography variant="h6">
                {categories.length}{' '}
                {`Categor${categories.length === 1 ? 'y' : 'ies'}`}
              </Typography>
              <MultiCircularProgress
                completed={categoriesCompleted}
                total={categories.length}
                size={72}
                fontSize={18}
              />
              <Typography>{categoriesCompleted} Completed</Typography>
            </Card>
            <Card
              variant="outlined"
              sx={{
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <Typography variant="h6">
                {totalHours} {`Hour${totalHours === 1 ? '' : 's'}`}
              </Typography>
              <MultiCircularProgress
                completed={completedHours}
                total={totalHours}
                size={72}
                fontSize={18}
              />
              <Typography>{completedHours} Completed</Typography>
            </Card>
            {liveCategory && (
              <Card
                variant="outlined"
                sx={{
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                }}
              >
                <Typography variant="h6">
                  {liveCategory.hours} Live{' '}
                  {`Hour${liveCategory.hours === 1 ? '' : 's'}`}
                </Typography>
                <MultiCircularProgress
                  completed={completedEventHours}
                  total={liveCategory.hours}
                  size={72}
                  fontSize={18}
                />
                <Typography>{completedEventHours} Completed</Typography>
              </Card>
            )}
          </Box>
          <Box sx={{ mt: 3 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                Categorized Courses
              </Typography>
              <Button
                color="secondary"
                sx={{
                  alignSelf: 'flex-start',
                }}
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setCategoryFormInfo({ categoryType: 'standard' })
                  setCategoryFormOpen(true)
                }}
              >
                Add Category
              </Button>
              <Button
                sx={{ alignSelf: 'flex-start' }}
                variant="contained"
                startIcon={<AddIcon />}
                disabled={!categories.length}
                onClick={() => setCourseFormOpen(true)}
              >
                Add Course
              </Button>
              <Button
                sx={{ alignSelf: 'flex-start' }}
                variant="contained"
                startIcon={<AddIcon />}
                disabled={!categories.length}
                onClick={() => setEventFormOpen(true)}
              >
                Add Live Class
              </Button>
            </Box>
            <Box
              sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}
            >
              {!categories.length && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  <Box
                    sx={{
                      p: 3,
                      bgcolor: 'background.paperDark',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      alignItems: 'center',
                    }}
                  >
                    <Avatar
                      sx={{
                        height: 72,
                        width: 72,
                        bgcolor: 'background.paper',
                      }}
                    >
                      <CategoryIcon fontSize="large" color="primary" />
                    </Avatar>
                    <Typography variant="h6">
                      No Categories Added Yet
                    </Typography>
                    <Typography
                      color="text.secondary"
                      variant="body1"
                      sx={{
                        textAlign: 'center',
                        maxWidth: 450,
                        lineHeight: '18px',
                      }}
                    >
                      Create your first cycle by clicking the button below. You
                      can add multiple cycles for different licenses,
                      certifications, and credentials, or even multiple cycles
                      for the same item if desired.
                    </Typography>
                    <Box sx={{ mt: 1.5, display: 'flex', gap: 1.5 }}>
                      <Button
                        sx={{ py: 1.25 }}
                        variant="contained"
                        size="large"
                        startIcon={<AddIcon />}
                        onClick={() => {
                          setCategoryFormInfo({ categoryType: 'standard' })
                          setCategoryFormOpen(true)
                        }}
                      >
                        Add First Category
                      </Button>
                      {!liveCategory && (
                        <>
                          <Typography
                            sx={{ alignSelf: 'center' }}
                            color="textSecondary"
                          >
                            OR
                          </Typography>
                          <Button
                            sx={{ py: 1.25 }}
                            color="secondary"
                            variant="contained"
                            size="large"
                            startIcon={<TemplateIcon />}
                            onClick={() => setTemplateFormOpen(true)}
                          >
                            Apply Template
                          </Button>
                        </>
                      )}
                    </Box>
                  </Box>
                </Box>
              )}
              <Box aria-live="polite" aria-atomic="true" className="sr-only">
                {isSavingOrder ? 'Saving…' : ''}
              </Box>
              {isSavingOrder && (
                <Box className="flex items-center gap-2">
                  <CircularProgress size={14} aria-label="Saving" />
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary' }}
                  >
                    Saving…
                  </Typography>
                </Box>
              )}
              <DragDropContext onDragEnd={handleCategoryDragEnd}>
                <Droppable droppableId="groups" type={GROUP_DRAG_TYPE}>
                  {(groupsProvided) => (
                    <Box
                      {...groupsProvided.droppableProps}
                      ref={groupsProvided.innerRef}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                      }}
                    >
                      {groups.map((group, groupIndex) => (
                        <Draggable
                          key={group}
                          draggableId={`group:${group}`}
                          index={groupIndex}
                          isDragDisabled={isSavingOrder || !hasGroups}
                        >
                          {(groupProv) => (
                            <Box
                              ref={groupProv.innerRef}
                              {...groupProv.draggableProps}
                            >
                              {hasGroups && (
                                <Box
                                  className="flex items-center gap-1"
                                  sx={{ mt: 0.5, mb: 0.75, ml: 0.5 }}
                                >
                                  <Box
                                    {...groupProv.dragHandleProps}
                                    aria-label={`Drag to reorder group ${group}`}
                                    className="flex items-center"
                                    sx={{
                                      cursor: isSavingOrder
                                        ? 'default'
                                        : 'grab',
                                      color: 'text.secondary',
                                    }}
                                  >
                                    <DragAndDropIcon fontSize="small" />
                                  </Box>
                                  <Typography
                                    color="text.secondary"
                                    sx={{ fontWeight: 'bold' }}
                                  >
                                    {group}
                                  </Typography>
                                </Box>
                              )}
                              <Droppable
                                droppableId={`${CATEGORY_DROPPABLE_PREFIX}${group}`}
                              >
                                {(provided) => (
                                  <Box
                                    {...provided.droppableProps}
                                    ref={provided.innerRef}
                                    sx={{
                                      display: 'flex',
                                      flexDirection: 'column',
                                      gap: 1.5,
                                    }}
                                  >
                                    {localCategories
                                      .filter(
                                        (c) =>
                                          (c.subcategory || UNGROUPED) ===
                                          group,
                                      )
                                      .map((category, index) => {
                                        let eventHours = category.events.reduce(
                                          (sum, ev) => sum + ev.hours,
                                          0,
                                        )
                                        let externalHours =
                                          category.externalCertificates.reduce(
                                            (sum, ec) => sum + ec.hours,
                                            0,
                                          )
                                        let completedCourseHours =
                                          category.courses.reduce(
                                            (sum, c) =>
                                              sum + (c.complete ? c.hours : 0),
                                            0,
                                          ) +
                                          eventHours +
                                          externalHours
                                        // Everything assigned, completed or not.
                                        let plannedHours =
                                          category.courses.reduce(
                                            (sum, c) => sum + c.hours,
                                            0,
                                          ) +
                                          eventHours +
                                          externalHours
                                        let allRequiredCoursesComplete =
                                          category.courses
                                            .filter((c) => c.required)
                                            .every((c) => c.complete)
                                        let categoryComplete =
                                          completedCourseHours >=
                                            category.hours &&
                                          allRequiredCoursesComplete

                                        return (
                                          <Draggable
                                            key={category.id}
                                            draggableId={category.id}
                                            index={index}
                                            isDragDisabled={isSavingOrder}
                                          >
                                            {(prov) => (
                                              <Card
                                                ref={prov.innerRef}
                                                {...prov.draggableProps}
                                                variant="outlined"
                                                sx={{
                                                  p: 2,
                                                  bgcolor:
                                                    'background.paperDark',
                                                }}
                                              >
                                                <Box
                                                  sx={{
                                                    pb: 0.5,
                                                    display: 'flex',
                                                    flexWrap: 'wrap',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                    borderBottom: '1px solid',
                                                    borderColor: 'divider',
                                                  }}
                                                >
                                                  <Box
                                                    {...prov.dragHandleProps}
                                                    aria-label={`Drag to reorder ${category.name}`}
                                                    className="flex items-center"
                                                    sx={{
                                                      cursor: isSavingOrder
                                                        ? 'default'
                                                        : 'grab',
                                                      color: 'text.secondary',
                                                    }}
                                                  >
                                                    <DragAndDropIcon fontSize="small" />
                                                  </Box>
                                                  <Box
                                                    sx={{
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      gap: 0.75,
                                                    }}
                                                  >
                                                    {categoryComplete && (
                                                      <VerifiedIcon color="success" />
                                                    )}
                                                    {category.required && (
                                                      <Tooltip title="Required by manager">
                                                        <ManagerIcon
                                                          fontSize="small"
                                                          color="secondary"
                                                        />
                                                      </Tooltip>
                                                    )}
                                                    <Typography
                                                      sx={{ fontSize: 18 }}
                                                    >
                                                      {category.name}
                                                    </Typography>
                                                  </Box>
                                                  {!category.required && (
                                                    <IconButton
                                                      color="primary"
                                                      size="small"
                                                      aria-label={`Edit category: ${category.name}`}
                                                      onClick={() => {
                                                        setCategoryFormInfo(
                                                          category,
                                                        )
                                                        setCategoryFormOpen(
                                                          true,
                                                        )
                                                      }}
                                                    >
                                                      <EditIcon />
                                                    </IconButton>
                                                  )}
                                                  <Box
                                                    sx={{ flexGrow: 1 }}
                                                  ></Box>
                                                  {!category.required && (
                                                    <Button
                                                      color="error"
                                                      startIcon={<DeleteIcon />}
                                                      onClick={() =>
                                                        setDeleteCategoryID(
                                                          category.id,
                                                        )
                                                      }
                                                    >
                                                      Delete Category
                                                    </Button>
                                                  )}
                                                </Box>
                                                <Box
                                                  sx={{
                                                    mt: 1,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                  }}
                                                >
                                                  <Box
                                                    sx={{
                                                      pl: 1,
                                                      flexGrow: 1,
                                                      color: 'text.secondary',
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      columnGap: 1,
                                                      flexWrap: 'wrap',
                                                    }}
                                                  >
                                                    <Typography
                                                      sx={{
                                                        fontWeight: 'bold',
                                                      }}
                                                    >
                                                      Course List
                                                    </Typography>
                                                    <Typography>
                                                      {'\u2022'}
                                                    </Typography>
                                                    <Typography>
                                                      {category.hours} Hour
                                                      {category.hours === 1
                                                        ? ''
                                                        : 's'}{' '}
                                                      Required
                                                    </Typography>
                                                    <Typography>
                                                      {'\u2022'}
                                                    </Typography>
                                                    <Typography>
                                                      {plannedHours} Hour
                                                      {plannedHours === 1
                                                        ? ''
                                                        : 's'}{' '}
                                                      Planned
                                                    </Typography>
                                                    <Typography>
                                                      {'\u2022'}
                                                    </Typography>
                                                    <Typography>
                                                      {completedCourseHours}{' '}
                                                      Hour
                                                      {completedCourseHours ===
                                                      1
                                                        ? ''
                                                        : 's'}{' '}
                                                      Completed
                                                    </Typography>
                                                    <Typography>
                                                      {'\u2022'}
                                                    </Typography>
                                                    <Typography>
                                                      Progress:{' '}
                                                      {categoryComplete
                                                        ? 100
                                                        : Math.round(
                                                            (completedCourseHours *
                                                              100) /
                                                              category.hours,
                                                          )}
                                                      %
                                                    </Typography>
                                                    {!allRequiredCoursesComplete && (
                                                      <Tooltip title="Even at 100% hourly progress, this category is not complete until all manager-required courses are finished.">
                                                        <IconButton
                                                          size="small"
                                                          aria-label="Required courses incomplete"
                                                          sx={{
                                                            ml: -0.75,
                                                            cursor: 'help',
                                                          }}
                                                        >
                                                          <InfoIcon
                                                            fontSize="small"
                                                            color="primary"
                                                          />
                                                        </IconButton>
                                                      </Tooltip>
                                                    )}
                                                    {/* The buffer bar shows planned hours, lighter than completed ones. */}
                                                    <LinearProgress
                                                      sx={{
                                                        flexGrow: 1,
                                                        minWidth: 50,
                                                        my: '10px',
                                                        mr: '1px',
                                                        // Buffer mode's track is the dashed element; a plain one
                                                        // keeps the unplanned remainder visible.
                                                        bgcolor: 'divider',
                                                        '& .MuiLinearProgress-dashed':
                                                          { display: 'none' },
                                                      }}
                                                      color={
                                                        categoryComplete
                                                          ? 'success'
                                                          : 'primary'
                                                      }
                                                      variant="buffer"
                                                      value={
                                                        categoryComplete
                                                          ? 100
                                                          : Math.min(
                                                              (completedCourseHours /
                                                                category.hours) *
                                                                100,
                                                              100,
                                                            )
                                                      }
                                                      valueBuffer={
                                                        categoryComplete
                                                          ? 100
                                                          : Math.min(
                                                              (plannedHours /
                                                                category.hours) *
                                                                100,
                                                              100,
                                                            )
                                                      }
                                                    />
                                                    <Button
                                                      color="primary"
                                                      size="small"
                                                      onClick={() => {
                                                        setCourseFormInfo({
                                                          categoryID:
                                                            category.id,
                                                        })
                                                        setCourseFormOpen(true)
                                                      }}
                                                    >
                                                      Add Course
                                                    </Button>
                                                  </Box>
                                                  <Box
                                                    sx={{
                                                      flexGrow: 1,
                                                      border: '1px solid',
                                                      borderColor: 'divider',
                                                      minHeight: 192,
                                                      maxHeight: 288,
                                                      overflow: 'scroll',
                                                    }}
                                                  >
                                                    {!category.courses
                                                      .length && (
                                                      <Box
                                                        sx={{
                                                          p: 1,
                                                          height: 192,
                                                          display: 'flex',
                                                          flexDirection:
                                                            'column',
                                                          justifyContent:
                                                            'center',
                                                          alignContent:
                                                            'center',
                                                        }}
                                                      >
                                                        <Avatar
                                                          sx={{
                                                            mb: 0.5,
                                                            height: 48,
                                                            width: 48,
                                                            bgcolor:
                                                              'background.paper',
                                                            alignSelf: 'center',
                                                          }}
                                                        >
                                                          <CourseIcon
                                                            fontSize="medium"
                                                            color="primary"
                                                          />
                                                        </Avatar>
                                                        <Typography align="center">
                                                          No Courses Added Yet
                                                        </Typography>
                                                        <Button
                                                          sx={{
                                                            alignSelf: 'center',
                                                            mt: 1,
                                                          }}
                                                          variant="contained"
                                                          startIcon={
                                                            <AddIcon />
                                                          }
                                                          disabled={
                                                            !categories.length
                                                          }
                                                          onClick={() => {
                                                            setCourseFormInfo({
                                                              categoryID:
                                                                category.id,
                                                            })
                                                            setCourseFormOpen(
                                                              true,
                                                            )
                                                          }}
                                                        >
                                                          Add Course
                                                        </Button>
                                                      </Box>
                                                    )}
                                                    {category.courses.map(
                                                      (courseData, i) => {
                                                        let {
                                                          id,
                                                          courseID,
                                                          hours,
                                                        } = courseData
                                                        let course =
                                                          courses.find(
                                                            (c) =>
                                                              c.id === courseID,
                                                          )

                                                        return (
                                                          <Box
                                                            key={courseData.id}
                                                            sx={{
                                                              p: 1.5,
                                                              display: 'flex',
                                                              flexWrap: 'wrap',
                                                              alignItems:
                                                                'center',
                                                              gap: 0.5,
                                                              bgcolor:
                                                                i % 2
                                                                  ? 'divider'
                                                                  : undefined,
                                                            }}
                                                          >
                                                            {!courseData.complete ? (
                                                              <UncheckedIcon aria-hidden="true" />
                                                            ) : (
                                                              <CheckedIcon
                                                                color="success"
                                                                aria-hidden="true"
                                                              />
                                                            )}
                                                            <span className="sr-only">
                                                              {courseData.complete
                                                                ? 'Completed'
                                                                : 'Not completed'}
                                                            </span>
                                                            <Box
                                                              sx={{
                                                                display: 'flex',
                                                                alignItems:
                                                                  'center',
                                                                gap: 0.5,
                                                              }}
                                                            >
                                                              {courseData.required && (
                                                                <Tooltip title="Required by manager">
                                                                  <ManagerIcon
                                                                    fontSize="small"
                                                                    color="secondary"
                                                                  />
                                                                </Tooltip>
                                                              )}
                                                              <Typography>
                                                                {course?.name}
                                                              </Typography>
                                                            </Box>
                                                            <Box
                                                              sx={{
                                                                flexGrow: 1,
                                                              }}
                                                            >
                                                              {course && (
                                                                <IconButton
                                                                  size="small"
                                                                  color="primary"
                                                                  aria-label={`Go to course ${course.name}`}
                                                                  onClick={() =>
                                                                    navigate(
                                                                      `/self-paced/course/${course.id}`,
                                                                    )
                                                                  }
                                                                >
                                                                  <LaunchIcon fontSize="small" />
                                                                </IconButton>
                                                              )}
                                                            </Box>
                                                            <Typography
                                                              sx={{
                                                                flexShrink: 0,
                                                              }}
                                                            >
                                                              {hours} Hour
                                                              {hours === 1
                                                                ? ''
                                                                : 's'}
                                                            </Typography>
                                                            {!courseData.required && (
                                                              <Box
                                                                sx={{
                                                                  my: -1,
                                                                  ml: 1.5,
                                                                  mr: -0.5,
                                                                  flexShrink: 0,
                                                                }}
                                                              >
                                                                <IconButton
                                                                  size="small"
                                                                  aria-label={`Edit course${course?.name ? `: ${course.name}` : ''}`}
                                                                  onClick={() => {
                                                                    setCourseFormInfo(
                                                                      {
                                                                        ...courseData,
                                                                        categoryID:
                                                                          category.id,
                                                                      },
                                                                    )
                                                                    setCourseFormOpen(
                                                                      true,
                                                                    )
                                                                  }}
                                                                >
                                                                  <EditIcon fontSize="small" />
                                                                </IconButton>
                                                                <IconButton
                                                                  size="small"
                                                                  aria-label={`Delete course${course?.name ? `: ${course.name}` : ''}`}
                                                                  onClick={() =>
                                                                    setDeleteCourseID(
                                                                      id,
                                                                    )
                                                                  }
                                                                >
                                                                  <DeleteIcon fontSize="small" />
                                                                </IconButton>
                                                              </Box>
                                                            )}
                                                          </Box>
                                                        )
                                                      },
                                                    )}
                                                    {(category.courses
                                                      .length === 1 ||
                                                      category.courses
                                                        .length === 3) && (
                                                      <Divider />
                                                    )}
                                                  </Box>
                                                  <LiveClassList
                                                    category={category}
                                                  />
                                                  <ExternalCertificateList
                                                    category={category}
                                                  />
                                                </Box>
                                              </Card>
                                            )}
                                          </Draggable>
                                        )
                                      })}
                                    {provided.placeholder}
                                  </Box>
                                )}
                              </Droppable>
                            </Box>
                          )}
                        </Draggable>
                      ))}
                      {groupsProvided.placeholder}
                    </Box>
                  )}
                </Droppable>
              </DragDropContext>
            </Box>
          </Box>
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 0.5 }}>
              Live Classes
            </Typography>
            {!liveCategory ? (
              <Card
                variant="outlined"
                sx={{
                  mt: 2,
                  px: 3,
                  py: 2.5,
                  bgcolor: 'background.paperDark',
                }}
              >
                <Typography variant="body2">
                  Recertification cycles with live education requirements can
                  track hours through a special category for courses & event
                  hours. This live category tracks events instead of courses; if
                  created, any event completed within this cycle's timeframe
                  will count toward its completion.
                </Typography>
                <Button
                  color="secondary"
                  sx={{
                    mt: 1.5,
                  }}
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => {
                    setCategoryFormInfo({
                      categoryType: 'live',
                      name: 'Live Instructor-Led',
                    })
                    setCategoryFormOpen(true)
                  }}
                >
                  Add Live Category
                </Button>
              </Card>
            ) : (
              <Card
                variant="outlined"
                sx={{ p: 2, mt: 2, bgcolor: 'background.paperDark' }}
              >
                <Box
                  sx={{
                    pb: 0.5,
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    gap: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
                    {completedEvents.length > 0 && liveCompleted && (
                      <VerifiedIcon
                        color="success"
                        sx={{
                          mr: 0.5,
                        }}
                      />
                    )}
                    <Typography
                      sx={{
                        fontSize: 18,
                      }}
                    >
                      {liveCategory.name}
                    </Typography>
                  </Box>
                  <IconButton
                    color="primary"
                    size="small"
                    aria-label={`Edit live category: ${liveCategory.name}`}
                    onClick={() => {
                      setCategoryFormInfo(liveCategory)
                      setCategoryFormOpen(true)
                    }}
                  >
                    <EditIcon />
                  </IconButton>
                  <Box sx={{ flexGrow: 1 }}></Box>
                  <Button
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => setDeleteCategoryID(liveCategory.id)}
                  >
                    Delete Category
                  </Button>
                </Box>
                <Box
                  sx={{
                    mt: 1,
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Box
                    sx={{
                      pl: 1,
                      pb: 0.75,
                      flexGrow: 1,
                      color: 'text.secondary',
                      display: 'flex',
                      alignItems: 'center',
                      columnGap: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Typography sx={{ fontWeight: 'bold' }}>
                      Live Class List
                    </Typography>
                    <Typography>{'\u2022'}</Typography>
                    <Typography>
                      {liveCategory.hours} Hour
                      {liveCategory.hours === 1 ? '' : 's'} Required
                    </Typography>
                    <Typography>{'\u2022'}</Typography>
                    <Typography>
                      {completedEventHours} Hour
                      {completedEventHours === 1 ? '' : 's'} Completed
                    </Typography>
                    <Typography>{'\u2022'}</Typography>
                    <Typography>
                      Progress:{' '}
                      {liveCompleted
                        ? 100
                        : Math.round(
                            (completedEventHours * 100) / liveCategory.hours,
                          )}
                      %
                    </Typography>
                    <LinearProgress
                      sx={{
                        flexGrow: 1,
                        minWidth: 50,
                        my: '10px',
                        mr: '1px',
                      }}
                      color={liveCompleted ? 'success' : 'primary'}
                      variant="determinate"
                      value={
                        liveCompleted
                          ? 100
                          : (completedEventHours / liveCategory.hours) * 100
                      }
                    />
                    {/* {completedEventHours > 0 && ( */}
                    <Button
                      size="small"
                      startIcon={<LaunchIcon />}
                      onClick={() => navigate('/live')}
                    >
                      View Live Classes
                    </Button>
                    {/* )} */}
                  </Box>
                  <Box
                    sx={{
                      flexGrow: 1,
                      border: '1px solid',
                      borderColor: 'divider',
                      minHeight: 192,
                      maxHeight: 288,
                      overflow: 'scroll',
                    }}
                  >
                    {!completedEvents.length && (
                      <Box
                        sx={{
                          p: 1,
                          height: 192,
                          display: 'flex',
                          flexDirection: 'column',
                          justifyContent: 'center',
                          alignContent: 'center',
                        }}
                      >
                        <Avatar
                          sx={{
                            mb: 0.5,
                            height: 48,
                            width: 48,
                            bgcolor: 'background.paper',
                            alignSelf: 'center',
                          }}
                        >
                          <EventIcon fontSize="medium" color="primary" />
                        </Avatar>
                        <Typography align="center">
                          No Live Classes Completed Yet
                        </Typography>
                        <Button
                          sx={{ alignSelf: 'center', mt: 1 }}
                          variant="outlined"
                          startIcon={<LaunchIcon />}
                          onClick={() => navigate('/live')}
                        >
                          View Live Classes
                        </Button>
                      </Box>
                    )}
                    {completedEvents.map((event, i) => (
                      <Box
                        key={event.id}
                        sx={{
                          p: 1.5,
                          display: 'flex',
                          flexWrap: 'wrap',
                          alignItems: 'center',
                          gap: 0.5,
                          bgcolor: i % 2 ? 'divider' : undefined,
                        }}
                      >
                        <CheckedIcon color="success" aria-hidden="true" />
                        <span className="sr-only">Completed</span>
                        <Typography sx={{ flexGrow: 1 }}>
                          {event.name}
                        </Typography>
                        <Typography sx={{ flexShrink: 0 }}>
                          {event.hours} Hour{event.hours === 1 ? '' : 's'}
                        </Typography>
                        <Tooltip title="Assign to Category" placement="top">
                          <IconButton
                            sx={{ p: 0, ml: 1 }}
                            color="primary"
                            size="small"
                            aria-label={`Assign ${event.name} to category`}
                            onClick={() => {
                              setEventFormInfo({ eventID: event.id })
                              setEventFormOpen(true)
                            }}
                          >
                            <AddIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ))}
                    {(completedEvents.length === 1 ||
                      completedEvents.length === 3) && <Divider />}
                  </Box>
                  <ExternalCertificateList category={liveCategory} />
                </Box>
              </Card>
            )}
          </Box>
        </Box>
      )}
      <TemplateForm
        cycleID={cycleID || ''}
        refetchCycles={baseRefetch}
        open={templateFormOpen}
        setOpen={() => setTemplateFormOpen(false)}
      />
      <CategoryForm
        cycleID={cycleID || ''}
        category={categoryFormInfo}
        refetchCycles={baseRefetch}
        open={categoryFormOpen}
        setOpen={() => {
          setCategoryFormOpen(false)
          setCategoryFormInfo(undefined)
        }}
      />
      <CourseForm
        cycleID={cycleID || ''}
        course={courseFormInfo}
        refetchCycles={baseRefetch}
        open={courseFormOpen}
        setOpen={() => {
          setCourseFormOpen(false)
          setCourseFormInfo(undefined)
        }}
      />
      <EventForm
        cycleID={cycleID || ''}
        event={eventFormInfo}
        refetchCycles={baseRefetch}
        open={eventFormOpen}
        setOpen={() => {
          setEventFormOpen(false)
          setEventFormInfo(undefined)
        }}
      />
      <ExternalCertificateForm
        cycleID={cycleID || ''}
        externalCertificate={externalCertFormInfo}
        refetchCycles={baseRefetch}
        open={externalCertFormOpen}
        setOpen={() => {
          setExternalCertFormOpen(false)
          setExternalCertFormInfo(undefined)
        }}
      />
      <ConfirmDeletion
        name="recertification cycle course"
        route={`/api/profile/recertification-cycle/course/${deleteCourseID}`}
        fetchEntity={refetches}
        open={!!deleteCourseID}
        setOpen={() => setDeleteCourseID(undefined)}
      />
      <ConfirmDeletion
        name="recertification cycle live class"
        route={`/api/profile/recertification-cycle/event/${deleteEventID}`}
        fetchEntity={refetches}
        open={!!deleteEventID}
        setOpen={() => setDeleteEventID(undefined)}
      />
      <ConfirmDeletion
        name="recertification cycle external certificate"
        route={`/api/profile/recertification-cycle/external-certificate/${deleteExternalCertID}`}
        fetchEntity={refetches}
        open={!!deleteExternalCertID}
        setOpen={() => setDeleteExternalCertID(undefined)}
      />
      <ConfirmDeletion
        name="recertification cycle category"
        route={`/api/profile/recertification-cycle/category/${deleteCategoryID}`}
        fetchEntity={refetches}
        open={!!deleteCategoryID}
        setOpen={() => setDeleteCategoryID(undefined)}
      />
      {NotificationComponent}
    </BasePage>
  )
}

export default CycleDetail
